<template>
    <div v-if="shift" class="shift-item">
        <!-- Итог выбранной смены: готовые часы табеля/плана, чипы статуса (RD-140). -->
        <div v-if="summary" class="shift-summary">
            <div class="shift-summary-line">
                <span v-if="summary.value !== null" class="shift-summary-value">{{ summary.value }}</span>
                <span class="shift-summary-text">{{ summary.text }}</span>
            </div>
            <div v-if="summary.note" class="shift-summary-note">{{ summary.note }}</div>
            <div v-if="statusChips.length" class="shift-chips">
                <span v-for="chip in statusChips" :key="chip.text" class="shift-chip" :class="`shift-chip-${chip.kind}`">{{ chip.text }}</span>
            </div>
            <div v-if="isSeriousViolation" role="status" class="shift-serious">
                <icon width="16" height="13" class="shift-serious-icon"><IconWarning /></icon>
                Серьёзное нарушение — проверьте факт
            </div>
        </div>

        <!-- План и факт: каждый в одну строку, поля 64 px. -->
        <div v-if="shouldShowFullEditor" class="shift-times">
            <span></span>
            <span class="shift-times-caption">Начало</span>
            <span></span>
            <span class="shift-times-caption">Конец</span>
            <span class="shift-times-caption shift-times-hours">Часы</span>

            <span class="shift-times-label">План</span>
            <div class="shift-time" :class="{ 'shift-time-disabled': editShiftDisable }">
                <time-picker v-model="start" @click="shiftFieldClick" :disabled="editShiftDisable"
                    v-model:is-valid="planStartTimeValid"
                    required
                    :bgclr="planTimeError && !start ? '#ffd5c3' : undefined" />
            </div>
            <span class="shift-times-dash">–</span>
            <div class="shift-time" :class="{ 'shift-time-disabled': editShiftDisable }">
                <time-picker v-model="end" @click="shiftFieldClick" :disabled="editShiftDisable"
                    v-model:is-valid="planEndTimeValid"
                    required
                    :bgclr="planTimeError && !end ? '#ffd5c3' : undefined" />
            </div>
            <span class="shift-times-hours shift-times-value">{{ planHoursText }}</span>

            <span class="shift-times-label" :class="{ 'shift-times-label-muted': timing.isUpcoming }">Факт</span>
            <div class="shift-time" :class="{ 'shift-time-disabled': isFactDisabled, 'shift-time-violated': timing.startViolated }">
                <span v-if="isFactDisabled && !startFact" class="shift-time-empty" aria-label="Факт, начало">—</span>
                <time-picker v-else :bgclr="timing.startViolated ? 'var(--color-violation-bg)' : undefined" v-model="startFact"
                    v-model:is-valid="factStartTimeValid"
                    @click="shiftFieldClick"
                    :disabled="isFactDisabled" />
            </div>
            <span class="shift-times-dash">–</span>
            <div class="shift-time" :class="{ 'shift-time-disabled': isFactDisabled, 'shift-time-violated': timing.endViolated }">
                <span v-if="isFactDisabled && !endFact" class="shift-time-empty" aria-label="Факт, окончание">—</span>
                <time-picker v-else :bgclr="timing.endViolated ? 'var(--color-violation-bg)' : undefined" v-model="endFact"
                    v-model:is-valid="factEndTimeValid"
                    @click="shiftFieldClick"
                    :disabled="isFactDisabled" />
            </div>
            <span class="shift-times-hours" :class="timing.isUpcoming ? 'shift-times-hint' : 'shift-times-value'">{{ factHoursText }}</span>
        </div>
        <div v-if="shouldShowFullEditor && (planTimeError || overtimeFactStartError)" class="shift-messages">
            <div v-if="planTimeError" class="text-xs text-warning">
                Заполните плановое время начала и окончания смены
            </div>
            <div v-if="overtimeFactStartError" class="text-xs text-warning">
                {{ overtimeFactStartErrorMessage }}
            </div>
        </div>

        <!-- Тип дня и должность в ряд; у КС — продолжительность. -->
        <div class="shift-fields">
            <div class="shift-fields-row">
                <div class="shift-field">
                    <div class="shift-field-caption">Тип дня</div>
                    <dropdown
                        v-if="!isDayStateDocumentShift"
                        v-model="dayType"
                        :options="dayTypes"
                        @click="shiftFieldClick"
                        :disabled="!canEditDayType || editShiftDisable"
                    >
                        {{ store.cutStr(dayType.name, 23) }}
                    </dropdown>
                    <div v-else class="shift-field-text">
                        {{ store.cutStr(currentDayStateDocumentTypeName, 23) }}
                    </div>
                </div>
                <div v-if="shouldShowDurationEditor" class="shift-field">
                    <div class="shift-field-caption">Продолжительность</div>
                    <div class="shift-time" :class="{ 'shift-time-disabled': !canEditDayStateDuration }">
                        <time-picker
                            v-model="durationDisplay"
                            @click="shiftFieldClick"
                            :disabled="!canEditDayStateDuration"
                        />
                    </div>
                </div>
                <div v-else-if="shouldShowFullEditor" class="shift-field">
                    <div class="shift-field-caption">Должность</div>
                    <dropdown v-model="position" :options="store.positions" @click="shiftFieldClick"
                        :disabled="editShiftDisable">
                        {{ store.cutStr(position.name, 23) }}
                    </dropdown>
                </div>
            </div>

            <BooleanSwitch
                v-if="shouldShowFullEditor"
                v-model="isOvertime"
                :disabled="!!(disableOvertime || editShiftDisable || isOvertimeLockedByDayStateDocument)"
                @click="shiftFieldClick"
            >Дополнительный табель</BooleanSwitch>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, toRaw } from 'vue'
import { DayType, Schedule, Shift } from '@/types'
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import TimePicker from '@/components/TimePicker.vue'
import { Option } from '@/types'
import IconWarning from '@/ui/icons/IconWarning.vue'
import BooleanSwitch from '@/ui/BooleanSwitch.vue'
import { decimalHoursToPickerDuration, formatDecimalHours, formatOptionalDecimalHoursForSchedule, pickerDurationToDecimalHours } from '@/utils/duration'
import { describeShiftTiming, getShiftPlanHours, useWallClockNow } from './weekSchedule'
import { useToast } from 'vue-toast-notification'

dayjs.extend(utc)

import { useScheduleStore } from '@/stores/scheduleStore'
import { useAuthStore } from '@/stores/authStore'

const store = useScheduleStore()
const authStore = useAuthStore()
const toast = useToast({ position: 'bottom-right' })

interface Props {
    shift: Shift | null,
    dayTypes: DayType[],
    schedule: Schedule,
    // Текущий состав дня в редакторе (включая draft) — для итога «за день».
    dayShifts?: Shift[]
}

const props = defineProps<Props>()
const shift = ref<any>(store.currentShift || props.shift || null)
const isOvertime = ref(!!shift.value?.is_overtime)
const start = ref<string | null>(shift.value?.plan_work_start_at ? dayjs.utc(shift.value.plan_work_start_at).format('HH:mm') : shift.value?.plan_work_start_at ?? null)
const end = ref<string | null>(shift.value?.plan_work_end_at ? dayjs.utc(shift.value.plan_work_end_at).format('HH:mm') : shift.value?.plan_work_end_at ?? null)
const startFact = ref<string | null>(null)
const endFact = ref<string | null>(null)
const planStartTimeValid = ref(true)
const planEndTimeValid = ref(true)
const factStartTimeValid = ref(true)
const factEndTimeValid = ref(true)
const disableOvertime = ref(false)
const planTimeError = ref(false)
const isFutureOrCurrentShift = computed(() => dayjs().isBefore(dayjs(shift.value?.plan_work_end_at)))
const isPastShift = computed(() => dayjs(shift.value?.date).isBefore(dayjs(), 'day'))
const isOwnShift = computed(() => props.schedule.user.id === authStore.userInfo.id)
const canEditDayType = computed(() => authStore.permissions?.can_edit_day_type)
const canEditFact = computed(() => (
    authStore.permissions?.can_edit_fact_time ||
    (!props.schedule.user.smart_checkin_possible && isPastShift.value)
))
const canEditDayStateDuration = computed(() => authStore.permissions?.can_edit_day_state_duration)
const editShiftDisable = computed(() => !authStore.permissions?.can_edit_shift_schedule && shift.value?.id)

const durationDisplay = ref<string | null>(decimalHoursToPickerDuration(props.shift?.duration) || null)

const dayType = ref<Option>({
    id: shift.value?.day_type || '',
    name: props.dayTypes.find(item => item.id === shift.value?.day_type)?.name || ''
})
const position = ref<Option>({
    id: shift.value?.position || '',
    name: store.positions.find(item => item.id === shift.value?.position)?.name || ''
})

const shiftDayType = computed(() => store.dayTypes.find((daytype) => daytype.id === shift.value?.day_type))
const currentDayStateDocumentTypeName = computed(() => {
    return shiftDayType.value?.name || 'Кадровое отклонение'
})

const isWorkday = computed(() => {
    return !shiftDayType.value?.is_dayoff
})
const isDayStateDocumentShift = computed(() => Boolean(shift.value?.day_state_document))
const hasDayStateDocumentInCurrentDay = computed(() => store.currentShifts.some((item) => Boolean(item?.day_state_document)))
const isRegularShiftNextToDayStateDocument = computed(() => !isDayStateDocumentShift.value && hasDayStateDocumentInCurrentDay.value)
const isOvertimeLockedByDayStateDocument = computed(() => isRegularShiftNextToDayStateDocument.value)
const shouldShowDurationEditor = computed(() => isDayStateDocumentShift.value)
const shouldShowFullEditor = computed(() => isWorkday.value && !isDayStateDocumentShift.value)
const requiresPlanTime = computed(() => shouldShowFullEditor.value)
const overtimeFactStartErrorMessage = 'Фактическое время начала дополнительной смены не может быть раньше её планового времени начала'
const factStartDate = computed(() => {
    const date: string = shift.value.date
    // У ночной дополнительной смены утренняя часть плана относится к следующим суткам.
    if (isOvertime.value && start.value && end.value && startFact.value &&
        end.value < start.value && startFact.value <= end.value) {
        return dayjs.utc(date).add(1, 'day').format('YYYY-MM-DD')
    }

    return date
})
// Текущая вкладка исключается по ссылке и по id: первая вкладка открывается копией смены.
const isCurrentEditedShift = (item: Shift | null | undefined) => (
    !!item && (toRaw(item) === toRaw(shift.value) || (!!item.id && item.id === shift.value?.id))
)
// Основная рабочая — не дополнительная, не кадровое состояние и не выходной тип дня.
const isMainWorkShift = (item: Shift | null | undefined) => (
    !!item &&
    !item.is_overtime &&
    !item.day_state_document &&
    !store.dayTypes.find((daytype) => daytype.id === item.day_type)?.is_dayoff
)
const hasMainWorkShiftInCurrentDay = computed(() => (
    store.currentShifts.some((item) => !isCurrentEditedShift(item) && isMainWorkShift(item))
))
const overtimeFactStartError = computed(() => {
    if (!isOvertime.value || !start.value || !startFact.value) return false
    // Ранний факт дополнительной ограничен только рядом с основной рабочей сменой дня.
    if (!hasMainWorkShiftInCurrentDay.value) return false

    return dayjs.utc(`${factStartDate.value} ${startFact.value}`)
        .isBefore(dayjs.utc(`${shift.value.date} ${start.value}`))
})
const areTimePickersValid = computed(() => (
    planStartTimeValid.value &&
    planEndTimeValid.value &&
    factStartTimeValid.value &&
    factEndTimeValid.value
))

startFact.value = (shift.value?.fact_work_start_at) ? dayjs.utc(shift.value.fact_work_start_at).format('HH:mm') : null
endFact.value = (shift.value?.fact_work_end_at) ? dayjs.utc(shift.value.fact_work_end_at).format('HH:mm') : null

const checkValidPosition = () => {
    if (position.value.id !== props.schedule.position.id) {
        isOvertime.value = true
        disableOvertime.value = true
    } else {
        disableOvertime.value = false
    }
}

const isTimeChanged = (oldTime: any, newTime: any, fieldName: string) => {
    return dayjs.utc(oldTime[fieldName]).format('HH:mm') !== dayjs.utc(newTime[fieldName]).format('HH:mm')
}

const isShiftChanged = computed(() => {
    // get original shift for tracking changes 
    const originalShift = store.originalShifts.find(shift => shift.id === store.currentShift?.id)

    if (originalShift) {
        // check plan and fact time for changes
        if (isTimeChanged(originalShift, store.currentShift, 'plan_work_start_at')) return true
        else if (isTimeChanged(originalShift, store.currentShift, 'plan_work_end_at')) return true
        else if (isTimeChanged(originalShift, store.currentShift, 'fact_work_start_at')) return true
        else if (isTimeChanged(originalShift, store.currentShift, 'fact_work_end_at')) return true
        // check position
        else if (originalShift.position !== store.currentShift?.position) return true
        // check day type
        else if (originalShift.day_type !== store.currentShift.day_type) return true
        // check overtime
        else if (originalShift.is_overtime !== store.currentShift.is_overtime) return true
        // check duration for day state shifts
        else if (originalShift.duration !== store.currentShift?.duration) return true
    }
    return false
})

const handleChangedShifts = () => {
    store.isCurrentShiftChanged = isShiftChanged.value
    if (store.currentShift?.id) {
        if (isShiftChanged.value) {
            store.addChangedShiftId(store.currentShift.id)
            // store.updateCurrentShifts(shift.value)
        }
        else {
            store.removeChangedShiftId(store.currentShift.id)
        }
    }
}

watch([position, dayType, start, end, startFact, endFact, isOvertime, durationDisplay, planStartTimeValid, planEndTimeValid, factStartTimeValid, factEndTimeValid], () => {
    if (!shift.value) return

    if (isOvertimeLockedByDayStateDocument.value && !isOvertime.value) {
        isOvertime.value = true
    }

    shift.value.position = position.value.id
    checkValidPosition()
    shift.value.day_type = dayType.value.id
    if (!isWorkday.value) {
        // Для нерабочих дней план null
        start.value = null
        end.value = null
        shift.value.plan_work_start_at = null
        shift.value.plan_work_end_at = null
    } else {
        if (dayjs(`${shift.value.date} ${start.value}`).isValid()) {
            shift.value.plan_work_start_at = dayjs.utc(`${shift.value.date} ${start.value}`).toISOString()
        }
        // add next day if time end less than start plan
        shift.value.plan_work_end_at = endTime(start.value, end.value, shift.value.date)

        if (dayjs(`${shift.value.date} ${startFact.value}`).isValid()) {
            shift.value.fact_work_start_at = dayjs.utc(`${factStartDate.value} ${startFact.value}`).toISOString()
        }

        if (startFact.value === null) shift.value.fact_work_start_at = startFact.value
        if (endFact.value === null) shift.value.fact_work_end_at = endFact.value

        if (dayjs(`${shift.value.date} ${endFact.value}`).isValid()) {
            // add next day if time end less than start fact
            shift.value.fact_work_end_at = endTime(startFact.value, endFact.value, factStartDate.value)
        }
    }

    shift.value.is_overtime = isOvertime.value

    if (shift.value.day_state_document) {
        shift.value.duration = pickerDurationToDecimalHours(durationDisplay.value)
    }

    // handle shift changes
    const isPlanTimeValid = validatePlanTime(false)
    handleChangedShifts()
    store.setCurrentShift(shift.value)
    if (!isPlanTimeValid || !areTimePickersValid.value) {
        store.isSaveShiftEnabled = false
        return
    }
    store.handleSaveButtonAvailability()
})

const validatePlanTime = (showError = true) => {
    const isValid = (!requiresPlanTime.value || (!!start.value && !!end.value)) &&
        planStartTimeValid.value &&
        planEndTimeValid.value &&
        factStartTimeValid.value &&
        factEndTimeValid.value

    if (isValid) planTimeError.value = false
    else if (showError) planTimeError.value = true

    if (showError && overtimeFactStartError.value) {
        toast.error(overtimeFactStartErrorMessage)
    }

    return isValid && !overtimeFactStartError.value
}

const endTime = (start: any, end: any, date: any) => {
    if (start < end) {
        return dayjs.utc(`${date} ${end}`).toISOString()
    } else if (start && end) {
        const nextDay = dayjs(date).add(1, 'day').format('YYYY-MM-DD')
        return dayjs.utc(`${nextDay} ${end}`).toISOString()
    }
}

// --- Итог и состояние выбранной смены (RD-140): только отображение готовых данных. ---
const now = useWallClockNow()
// Готовые fact_seconds табеля по shift ID: неделя — месячные табели её дат, месяц — табель открытого месяца.
const shiftHours = computed(() => (store.isWeekPeriod ? store.weekShiftHours : store.scheduleShiftHours))
// Значение табеля показывается, только если его plan/fact времена совпадают с текущими (в т.ч. draft) временами.
const timingOf = (item: Shift) => describeShiftTiming(item, now.value, item.id ? shiftHours.value.get(item.id) : undefined)
const timing = computed(() => timingOf(shift.value))
const formatHours = (hours: number) => formatDecimalHours(hours, 'schedule')
const isFactDisabled = computed(() => Boolean(!canEditFact.value || isFutureOrCurrentShift.value || isOwnShift.value || editShiftDisable.value))

const planHoursText = computed(() => formatHours(getShiftPlanHours(shift.value)))
const factHoursText = computed(() => {
    if (timing.value.isUpcoming) return 'появится после смены'
    if (timing.value.isInProgress) return 'идёт'
    return timing.value.factHoursValue === null ? '—' : formatHours(timing.value.factHoursValue)
})

// Рабочие смены дня (без КС и без плана) — для «за день».
const dayWorkShifts = computed(() => (props.dayShifts ?? [shift.value])
    .filter((item): item is Shift => Boolean(item && !item.day_state_document && item.plan_work_start_at && item.plan_work_end_at)))
const dayPlanTotal = computed(() => dayWorkShifts.value.reduce((sum, item) => sum + getShiftPlanHours(item), 0))
// Итог дня — только если известны часы всех его смен.
const dayFactTotal = computed(() => {
    const values = dayWorkShifts.value.map(item => timingOf(item).factHoursValue)
    return values.some(value => value === null) ? null : values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
})
const isMultiShiftDay = computed(() => dayWorkShifts.value.length > 1)

const summary = computed<{ value: string | null, text: string, note: string } | null>(() => {
    if (isDayStateDocumentShift.value) {
        // Готовая продолжительность КС (в т.ч. 0), без расчёта отработанных рабочих часов.
        const plan = dayWorkShifts.value.length ? ` · за день ${formatHours(dayPlanTotal.value)} по плану` : ''
        return {
            value: formatOptionalDecimalHoursForSchedule(shift.value.duration) ?? '—',
            text: `учтено по кадровому состоянию${plan}`,
            // Период документа в данных редактора не приходит (только ID) — не показываем.
            note: currentDayStateDocumentTypeName.value,
        }
    }
    if (!shouldShowFullEditor.value) return null

    const current = timing.value
    const plan = planHoursText.value
    if (current.isUpcoming) {
        return {
            value: plan,
            text: `по плану${isMultiShiftDay.value ? ` · за день ${formatHours(dayPlanTotal.value)}` : ''}`,
            note: '',
        }
    }
    // Пока смена идёт, отработанное число не показываем.
    if (current.isInProgress) return { value: null, text: `по плану ${plan}`, note: '' }

    const day = isMultiShiftDay.value
        ? ` · за день ${dayFactTotal.value === null ? '—' : formatHours(dayFactTotal.value)} из ${formatHours(dayPlanTotal.value)}`
        : ''
    return {
        value: current.factHoursValue === null ? '—' : formatHours(current.factHoursValue),
        text: `отработано из ${plan}${day}`,
        note: '',
    }
})

const generalStatus = computed(() => shift.value?.violation?.general_status)
const isSeriousViolation = computed(() => timing.value.hasGeneralViolation && generalStatus.value === 'warning')
// Чипы: тексты нарушений — start_msg/end_msg backend как есть, без собственных порогов и разбора минут.
const statusChips = computed(() => {
    if (!shouldShowFullEditor.value) return []
    const current = timing.value
    const chips: { text: string, kind: 'waiting' | 'violation' | 'moderate' | 'ok' }[] = []
    if (current.isUpcoming) chips.push({ text: 'Запланирована', kind: 'waiting' })
    if (current.isInProgress) chips.push({ text: 'Идёт', kind: 'waiting' })
    if (current.isNoMarks) chips.push({ text: 'Нет отметок', kind: 'waiting' })
    current.violationMessages.forEach(text => chips.push({ text, kind: 'violation' }))
    if (current.violationCount && !current.violationMessages.length) chips.push({ text: 'Нарушение', kind: 'violation' })
    if (current.hasGeneralViolation && generalStatus.value === 'moderate') chips.push({ text: 'Смена с нарушениями', kind: 'moderate' })
    const isFinishedClean = !current.isUpcoming && !current.isInProgress && !current.isNoMarks && Boolean(current.factEndAt)
        && !current.violationCount && generalStatus.value === 'ok'
    if (isFinishedClean) chips.push({ text: 'Без нарушений', kind: 'ok' })
    return chips
})

const shiftFieldClick = () => {
    store.shiftFieldsClicked = true
}

onMounted(() => {
    if (!shift.value) return
    if (!store.currentShift) {
        store.setCurrentShift(shift.value)
    }
    if (isOvertimeLockedByDayStateDocument.value) {
        isOvertime.value = true
        shift.value.is_overtime = true
    }

    shift.value.plan_work_end_at = endTime(start.value, end.value, shift.value.date)
    checkValidPosition()
})

defineExpose({
    validatePlanTime
})

</script>

<style scoped>
.shift-item {
    display: flex;
    flex-direction: column;
    gap: 20px;
    color: var(--color-text-main);
}

.shift-summary {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-bg-muted);
}

.shift-summary-line {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    font-variant-numeric: tabular-nums;
}

.shift-summary-value {
    font-size: 28px;
    line-height: 30px;
    font-weight: 700;
}

.shift-summary-text {
    padding-bottom: 3px;
    font-size: 13px;
    color: var(--color-text-muted);
}

.shift-summary-note {
    font-size: 13px;
    line-height: 140%;
}

.shift-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.shift-chip {
    padding: 2px 8px;
    border-radius: 100px;
    font-size: 12px;
    line-height: 16px;
}

.shift-chip-violation {
    background-color: var(--color-violation-bg);
    color: var(--color-text-main);
}

.shift-chip-moderate {
    background-color: var(--color-warning-bg);
    color: var(--color-text-main);
}

.shift-chip-waiting {
    background-color: var(--color-status-chip-waiting-bg);
    color: var(--color-status-chip-waiting-text);
}

.shift-chip-ok {
    background-color: var(--color-status-chip-open-bg);
    color: var(--color-status-chip-open-text);
}

.shift-serious {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
}

.shift-serious-icon {
    color: var(--color-danger);
}

.shift-times {
    display: grid;
    grid-template-columns: 48px 64px 12px 64px 1fr;
    gap: 8px;
    align-items: center;
    font-variant-numeric: tabular-nums;
}

.shift-times-caption {
    font-size: 12px;
    color: var(--color-text-muted);
}

.shift-times-label {
    font-size: 13px;
    font-weight: 600;
}

.shift-times-label-muted {
    color: var(--color-text-muted);
}

.shift-times-dash {
    text-align: center;
    color: var(--color-text-muted);
}

.shift-times-hours {
    text-align: right;
    white-space: nowrap;
}

.shift-times-value {
    font-size: 13px;
    font-weight: 600;
}

.shift-times-hint {
    font-size: 12px;
    color: var(--color-text-muted);
}

.shift-messages {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: -12px;
}

/* Общий TimePicker в узком поле 64 px, текст по центру (локально, без изменения компонента). */
.shift-time {
    width: 64px;
}

.shift-time :deep(.tp-root) {
    width: 64px;
    padding: 0;
    justify-content: center;
    font-size: 13px;
    color: var(--color-text-main);
}

.shift-time :deep(.tp-input) {
    width: 22px;
}

.shift-time-violated :deep(.tp-root) {
    border-color: var(--color-warning);
}

.shift-time-disabled {
    opacity: .5;
}

.shift-time-disabled :deep(input) {
    cursor: not-allowed;
}

.shift-time-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 26px;
    border-radius: 4px;
    background-color: var(--color-bg-muted);
    font-size: 13px;
    color: var(--color-text-muted);
    cursor: not-allowed;
}

.shift-fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.shift-fields-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 15px 20px;
}

.shift-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.shift-field-caption {
    font-size: 12px;
    color: var(--color-text-muted);
}

.shift-field-text {
    display: flex;
    align-items: center;
    height: 26px;
    font-size: 13px;
}
</style>

<style>
/* noinspection CssUnusedSymbol */
.text-moderate {
    color: #FED36B
}

/* noinspection CssUnusedSymbol */
.text-ok {
    color: #a6bd63
}

/* noinspection CssUnusedSymbol */
.text-warning {
    color: #FA7E48
}
</style>
